import { gql } from '@apollo/client'

export const myFunds = gql`
  query MyFunds {
    me {
      id
      funds {
        id
        name
        number
        closed
      }
    }
  }
`

export const myAccessibleFunds = gql`
  query MyAccessibleFunds {
    me {
      id
      accessibleFunds {
        id
        name
        number
        userName
      }
    }
  }
`

export const advisorAccessibleFunds = gql`
  query AdvisorAccessibleFunds {
    me {
      id
      advisorAccessibleFunds {
        id
        name
        number
        totalBalance
        givingWalletBalance
        investmentWalletBalance
        customPermissions
        ownerName
        securitiesNeedingAttention {
          id
          amount
          description
          state
          createdAt
        }
        fundClaimInvite {
          id
          status
          statusLabel
          expiresAt
          sentAt
          claimedAt
        }
      }
    }
  }
`

export const allMFunds = gql`
  query allMFunds {
    items: allMFunds {
      id
      name
      number
      taxReceiptYears
      dafTransfers {
        id
        __typename
      }
      createdAt
      totalBalance
      givingWalletBalance
      investmentWalletBalance
      managingUsers {
        id
        __typename
      }
      userId
      legacyId
      userName
      __typename
    }
    total: _allMFundsMeta {
      count
    }
  }
`

export const myFund = gql`
  query MyFund($fundId: Int) {
    me {
      id
      fund(id: $fundId) {
        id
        name
        number
        closed
        userName
        fundProfile {
          identityKind
          portalName
        }
        totalBalance
        givingWalletBalance
        investmentWalletBalance
        taxReceiptYears
        netTotalGifts
        netTotalGiftsYTD
        netTotalGiftsPreviousYear
        netTotalGrantRequests
        netTotalGrantRequestsYTD
        netTotalGrantRequestsPreviousYear
        netTotalContributionsGiving
        netTotalContributionsInvestment
        netTotalContributionsGivingYTD
        netTotalContributionsGivingPreviousYear
        netTotalContributionsInvestmentYTD
        isGenerosityFund
        uniqueCharitiesCount
        givingGoals {
          amount
        }
      }
    }
  }
`

export const myFundActivity = gql`
  query MyFundActivity($fundId: Int) {
    me {
      id
      fund(id: $fundId) {
        id
        name
        # Existence-check field only -- investment-unlinked-activity.jsx needs
        # to know whether it's the donor's ONLY card (no real Investment
        # Accounts at all, just this unlinked bucket) to decide whether
        # showing a Back arrow makes sense.
        investmentPortfolios {
          id
        }
        dafTransfers {
          id
          amount
          fee
          netAmount
          sentTo
          state
          tip
          receiveToken
          type
          description
          initiatingFundId
          initiatingFund {
            id
            fundProfile {
              portalName
              craRecognisedName
            }
          }
          initiatingUser {
            isOrganization
            corporateName
            profile {
              firstName
              middleInitials
              lastName
            }
          }
          initiatingUserEmail
          receivingFundId
          receivingFund {
            id
            fundProfile {
              portalName
              craRecognisedName
            }
          }
          receivingUser {
            isOrganization
            corporateName
            profile {
              firstName
              middleInitials
              lastName
            }
          }
          createdAt
          canceledAt
          receivedAt
          usedGiveGenius
        }
        balanceAdjustments {
          id
          amount
          description
          walletType
          portfolioId
          investmentTransferId
          createdAt
        }
        # FLIGHT-295 follow-up: shown as its own activity row (Requested/
        # Approved/In Transit/Complete/Cancelled) from the moment it's
        # Requested, before any BalanceAdjustment exists yet -- see
        # investment-activity-helpers.js's investmentTransferDirection/
        # investmentTransferDescription. Once money actually moves, the
        # resulting BalanceAdjustment row above is suppressed client-side via
        # its investmentTransferId (avoids showing the same money movement
        # twice).
        investmentTransfers {
          id
          fromWalletType
          fromFundId
          fromPortfolioId
          toWalletType
          toFundId
          toPortfolioId
          amount
          currentStep
          dateRequested
          dateApproved
          dateInTransit
          datePosted
          updatedAt
          # Needed to name the firm in investmentTransferDescription's
          # "...at <firm>"/"...Investment: <firm>" wording -- only the side
          # actually walletType INVESTMENT resolves to a non-null portfolio.
          fromPortfolio {
            id
            custodialNumber
            financialFirm {
              id
              name
            }
          }
          toPortfolio {
            id
            custodialNumber
            financialFirm {
              id
              name
            }
          }
        }
        grantRequests {
          id
          amount
          tip
          netAmount
          state
          description: grantNote
          processingNote
          createdAt
          processingDate
          anonymous
          recurrentGrantRequestId
          usedGiveGenius
          charity {
            id
            accountName
          }
        }
        contributions {
          id
          amount
          fee
          netAmount
          state
          description
          paymentType
          walletType
          portfolioId
          createdAt
          receiptedDate
          taxReceipted
          recurrentContributionId
          usedGiveGenius
          paymentMethod {
            id
            paymentType
            details
          }
          grantRequest {
            amount
            charity {
              accountName
            }
          }
          dafTransfer {
            amount
            sentTo
          }
        }
        recentCharities {
          id
          accountName
          mailingAddress
          city
          province
        }
      }
    }
  }
`

// FLIGHT-296: one card per Investment Portfolio (a donor can have several) on
// the Investment Account activity tab, each with its own drill-down activity
// feed. Kept separate from myFund/myFundActivity (rather than adding these
// fields there) since this is the only tab that needs per-portfolio detail --
// same "one query per tab" convention myFundActivity/myRecurring already follow.
export const myFundInvestmentPortfolios = gql`
  query MyFundInvestmentPortfolios($fundId: Int) {
    me {
      id
      fund(id: $fundId) {
        id
        investmentWalletBalance
        investmentPortfolios {
          id
          custodialNumber
          balance
          lastStatementDate
          custodian {
            name
          }
          financialFirm {
            name
          }
          advisoryTeam {
            name
          }
          financialAdvisor {
            portalName
            user {
              email
              profile {
                firstName
                lastName
              }
            }
          }
        }
        # Minimal existence-check fields only -- just enough to decide
        # whether the "Other Investment Wallet Activity" card should render
        # at all. The card's own drill-down (investment-unlinked-activity.jsx)
        # fetches the real rows itself via myFundActivity. portfolioId is
        # needed too, since only a portfolioId-less Contribution counts as
        # "unlinked" (see hasUnlinkedInvestmentActivity).
        contributions {
          id
          walletType
          portfolioId
        }
        balanceAdjustments {
          id
          walletType
          portfolioId
        }
      }
    }
  }
`

// Fetched separately from the summary query above, only when a "View
// Activity" drill-down is opened -- the summary cards don't need any of
// these nested collections. Fetches every portfolio's activity in one round
// trip (rather than adding a portfolioId filter arg server-side) since a
// donor typically has only 1-3 portfolios; the drill-down page picks its one
// portfolio out of the list client-side by :portfolioId, same "fetch for the
// fund, filter client-side" convention giving-wallet.jsx/investment-account.jsx
// already use for their own row filtering.
export const investmentPortfolioActivity = gql`
  query InvestmentPortfolioActivity($fundId: Int) {
    me {
      id
      fund(id: $fundId) {
        id
        # Existence-check fields only, mirroring myFundInvestmentPortfolios --
        # this page needs to know whether it's the donor's ONLY card (this one
        # portfolio, no unlinked activity, nothing else to go Back to) so it
        # can decide whether to show the Back arrow at all. portfolioId is
        # needed too, since only a portfolioId-less Contribution counts as
        # "unlinked" (see hasUnlinkedInvestmentActivity).
        contributions {
          id
          walletType
          portfolioId
        }
        balanceAdjustments {
          id
          walletType
          portfolioId
        }
        # FLIGHT-295 follow-up: fetched once at the fund level (this page
        # filters client-side per portfolio by fromPortfolioId/toPortfolioId,
        # same "fetch for the fund, filter client-side" convention this file
        # already uses) -- see investment-activity-helpers.js.
        investmentTransfers {
          id
          fromWalletType
          fromPortfolioId
          toWalletType
          toPortfolioId
          amount
          currentStep
          dateRequested
          dateApproved
          dateInTransit
          datePosted
          updatedAt
          # Needed to name the firm in investmentTransferDescription's
          # "...at <firm>"/"...Investment: <firm>" wording -- only the side
          # actually walletType INVESTMENT resolves to a non-null portfolio.
          fromPortfolio {
            id
            custodialNumber
            financialFirm {
              id
              name
            }
          }
          toPortfolio {
            id
            custodialNumber
            financialFirm {
              id
              name
            }
          }
        }
        investmentPortfolios {
          id
          custodialNumber
          balance
          lastStatementDate
          financialFirm {
            name
          }
          advisoryTeam {
            name
          }
          financialAdvisor {
            portalName
            user {
              email
              profile {
                firstName
                lastName
              }
            }
          }
          balanceAdjustments {
            id
            amount
            description
            investmentTransferId
            createdAt
          }
          # FLIGHT-299: a Contribution can now post directly to this specific
          # portfolio -- same field set formatContributionDescription needs,
          # mirroring myFundActivity's fund-wide contributions selection.
          # state added for FLIGHT-550: only a 'cleared' Contribution counts
          # toward the running balance (see rows useMemo).
          contributions {
            id
            amount
            fee
            netAmount
            state
            description
            paymentType
            receiptedDate
            state
            paymentMethod {
              id
              paymentType
              details
            }
          }
        }
      }
    }
  }
`

export const myRecurring = gql`
  query MyRecurring($fundId: Int) {
    me {
      id
      fund(id: $fundId) {
        id
        isGenerosityFund
        recurrentGrantRequests {
          id
          amount
          tip
          grantNote
          frequency
          dayInMonth
          monthInYear
          nextScheduledGrant
          state
          anonymous
          createdAt
          paymentType
          usedGiveGenius
          linkedRecurrentContributionId
          linkedRecurrentContributionAmount
          linkedRecurrentContributionFrequency
          charity {
            id
            accountName
          }
        }
        recurrentContributions {
          id
          amount
          frequency
          nextScheduledCharge
          state
          dayInMonth
          monthInYear
          createdAt
          paymentType
          usedGiveGenius
          linkedRecurrentGrantRequestId
          linkedRecurrentGrantRequestAmount
          linkedRecurrentGrantRequestTip
          linkedRecurrentGrantRequestFrequency
          linkedRecurrentGrantRequestCharityName
          paymentMethod {
            id
            paymentType
            details
          }
        }
        recurrentDafTransfers {
          id
          amount
          state
          frequency
          dayInMonth
          monthInYear
          nextScheduledTransfer
          createdAt
          receivingFund {
            id
            name
            fundProfile {
              portalName
              craRecognisedName
            }
          }
        }
        recurrentDafTransfersIncoming {
          id
          amount
          state
          frequency
          dayInMonth
          monthInYear
          nextScheduledTransfer
          createdAt
          initiatingFund {
            id
            name
            fundProfile {
              portalName
              craRecognisedName
            }
          }
          initiatingUser {
            isOrganization
            corporateName
            profile {
              firstName
              middleInitials
              lastName
            }
          }
        }
      }
    }
  }
`

export const generosityFund = gql`
  query generosityFund($fundId: Int!) {
    generosityFund(fundId: $fundId) {
      id
      name
    }
  }
`

export const getLogo = gql`
  query getLogo($fundId: Int!) {
    getLogo(fundId: $fundId) {
      id
      name
      logo
    }
  }
`

export const searchGenerosityFunds = gql`
  query searchGenerosityFunds($searchString: String!) {
    searchGenerosityFunds(searchString: $searchString) {
      id
      name
    }
  }
`

export const canGiveAsGuest = gql`
  query canGiveAsGuest($email: String!) {
    canGiveAsGuest(email: $email)
  }
`

export const getFundSuccessionInstructions = gql`
  query GetFundSuccessionInstructions($fundId: Int!) {
    getFundSuccessionInstructions(fundId: $fundId) {
      id
      fundId
      successors {
        id
        firstName
        lastName
        email
        address
        phoneNumber
        relationshipToYou
        dateOfBirth
        allocationPercentage
        alternativeAction
      }
      charities {
        id
        charityName
        registrationNumber
        designation
        allocationPercentage
        timeFrame
        timeFrameMonths
        timeFrameYears
        alternativeAction
      }
      createdAt
      updatedAt
    }
  }
`

export const myRequestedSecuritiesContributions = gql`
  query MyRequestedSecuritiesContributions($fundId: Int!) {
    myRequestedSecuritiesContributions(fundId: $fundId) {
      id
      amount
      description
      state
      paymentType
      createdAt
    }
  }
`
